import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from '@emotion/styled';
import { Button, Container } from '@mui/material';
import { FocusTheme } from '@/components/Focus/configs/theme';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { clearOrderPlacement } from '@/redux/slices/orderPlacementSlice';
import {
  clearNewSchedule,
  getFocusRightsValidate,
  getFocusRightsPossible,
  setRightsTask,
  setScheduleData,
  setScheduleGroups
} from '@/redux/slices/focusNewScheduleSlice';
import { clearTitle, setActiveTitle } from '@/redux/slices/titleSlice';
import { setActiveTitleId } from '@/redux/slices/titleV2Slice';
import { getData } from '@/redux/slices/dataTableSlice';
import { selectVersions } from '@/redux/selectors/orderPlacementSelectors';
import { selectFocusNewScheduleSlice, selectFocusNewScheduleView, selectLaunchList } from '@/redux/selectors/focusNewScheduleSelectors';
import { useToast } from '@/components/_common/Toast';
import useConfirmationPrompt from '@/components/_hooks/useConfirmationPrompt';
import { defaultAvailEndDate } from '@/components/Focus/constants';
import axios from '@/_utils/axios';
import NewScheduleTitle from './NewScheduleTitle';
import OPTitleSearch from './TitleSearch/OPTitleSearch';
import ScheduleDetails from './ScheduleDetails/ScheduleDetails';
import ReviewSchedule from './ReviewSchedule/ReviewSchedule';
import { ActionDialog } from '@/components/Focus/ActionDialog';
import { ScheduleRow } from './ReviewSchedule/ReviewScheduleListAccordion';
import type { SchedulingPostData } from '@/redux/slices/slice_types';

type GloabalSchedulePostData = {
  customer_id: number | string;
  schedulings: {
    version_id: number;
    primary_av_lang: string;
    license: string;
    window_start: string;
    window_end: string;
    territory: string[];
    audio_languages: string[];
    timed_text_languages: string[];
  }[];
  platforms: string[];
};

const NewSchedule = () => {
  const history = useHistory();
  const { success, error } = useToast();
  const launchList = useAppSelector(selectLaunchList);
  const { dialog, setDialogVisible, dialogVisible } = useConfirmationPrompt({
    title: 'Unsaved Schedule Changes',
    message: 'You have unsaved changes. Are you sure you want to lose this schedule?',
    onCancel: () => {
      setDialogVisible(false);
    },
    onConfirm: () => {
      setDialogVisible(false);
      history.push('/focus/schedule');
    }
  });
  const dispatch = useAppDispatch();
  const view = useAppSelector(selectFocusNewScheduleView);
  const selectedVersions = useAppSelector(selectVersions);
  const {
    selectedPlatforms,
    frequency,
    startDate,
    endDate,
    selectedTerritories,
    selectedDubs,
    selectedSubs,
    selectedLicense,
    hasReviewChanges,
    scheduleGroups
  } = useAppSelector(selectFocusNewScheduleSlice);

  const [isBackConfirmOpen, setIsBackConfirmOpen] = useState(false);
  const [isCreatingSchedule, setCreatingSchedule] = useState(false);
  const [isIncrementalLaunchWorkspaceOpen, setIsIncrementalLaunchWorkspaceOpen] = useState(false);

  const setView = (newView: 'search' | 'details' | 'review') => {
    dispatch(setScheduleData({ view: newView }));
  };

  const isNextDisabled = () => {
    if (view === 'search') return selectedVersions.length === 0;
    if (view === 'details')
      return (
        selectedPlatforms.length === 0 ||
        !frequency ||
        !startDate ||
        selectedTerritories.length === 0 ||
        selectedLicense.length === 0 ||
        selectedDubs.length === 0 ||
        selectedSubs.length === 0
      );
    if (view === 'review') return isCreatingSchedule || isIncrementalLaunchWorkspaceOpen;
    return false;
  };

  const handleNextClick = () => {
    if (view === 'search') {
      // Check if we have rights for the selected versions using default values
      dispatch(
        getFocusRightsPossible({
          title_id: selectedVersions.map((v) => v.id),
          license: ['svod'],
          availed_platforms: ['oando'],
          term_start__gte: '2020-01-01T00:00:00Z',
          term_end__lt: defaultAvailEndDate
        })
      )
        .then((result) => {
          // Check if we got valid territories data
          if (result.payload?.territories && Object.keys(result.payload.territories).length > 0) {
            // We have rights, set task to completed
            dispatch(setRightsTask({ status: 'completed', id: null }));
          } else {
            // No rights or empty territories, create a task
            axios
              .post('/focus/rights/tasks/create/', { title_id: selectedVersions.map((v) => v.id) }, { baseURL: '/api/v1.1' })
              .then((resp) => {
                if (resp.data?.success && resp.data.data?.task_id) {
                  dispatch(setRightsTask({ status: 'pending', id: resp.data.data?.task_id }));
                } else {
                  dispatch(setRightsTask({ status: 'completed', id: null }));
                }
              })
              .catch(() => {
                dispatch(setRightsTask({ status: 'completed', id: null }));
              });
          }
        })
        .catch(() => {
          // Error occurred, fall back to creating a task
          axios
            .post('/focus/rights/tasks/create/', { title_id: selectedVersions.map((v) => v.id) }, { baseURL: '/api/v1.1' })
            .then((resp) => {
              if (resp.data?.success && resp.data.data?.task_id) {
                dispatch(setRightsTask({ status: 'pending', id: resp.data.data?.task_id }));
              } else {
                dispatch(setRightsTask({ status: 'completed', id: null }));
              }
            })
            .catch(() => {
              dispatch(setRightsTask({ status: 'completed', id: null }));
            });
        });
      setView('details');
    }
    if (view === 'details') {
      dispatch(
        getFocusRightsValidate({
          title_id: selectedVersions.map((v) => v.id),
          license: [selectedLicense],
          availed_platform: selectedPlatforms,
          term_start__gte: startDate,
          term_end__lt: endDate || defaultAvailEndDate,
          territories: selectedTerritories.map((t) => t.toLowerCase()),
          audio: selectedDubs.map((d) => ({ element_subtype: 'full', language: d.toLowerCase() })),
          timedtext: selectedSubs.map((s) => ({ element_subtype: 'full-mix', language: s.toLowerCase() }))
        })
      );
      dispatch(setScheduleData({ selectedVersions: selectedVersions.map((v) => v.id) }));
      setView('review');
    }
    if (view === 'review') {
      handleCreateSchedule();
    }
  };

  const handleBackClick = () => {
    if (view === 'details') {
      setView('search');
      return;
    }

    if (view === 'review') {
      const hasIncrementalLaunches = launchList.some((launch) => launch.type === 'incremental');

      if (hasReviewChanges || hasIncrementalLaunches) {
        setIsBackConfirmOpen(true);
        return;
      }

      setView('details');
    }
  };

  const handleCloseBackConfirm = () => setIsBackConfirmOpen(false);

  const handleConfirmBack = () => {
    setIsBackConfirmOpen(false);
    setIsIncrementalLaunchWorkspaceOpen(false);

    dispatch(
      setScheduleData({
        hasReviewChanges: false,
        launchList: []
      })
    );
    dispatch(setScheduleGroups([]));
    setView('details');
  };

  const handleCreateSchedule = async () => {
    setCreatingSchedule(true);
    if (scheduleGroups?.length === 0 || launchList?.length === 0) {
      error('No Schedule Found. Please go back and ensure you have selected valid details.');
      setCreatingSchedule(false);
      return;
    }

    const scheduleRequests: Array<Promise<any>> = [];

    const calculateLaunchDates = (schedule: ScheduleRow) => {
      const launchDates: Date[] = [];

      launchList.forEach((launch, index) => {
        if (index === 0) {
          const baseDate = schedule.occurrenceStartDate ? new Date(schedule.occurrenceStartDate) : new Date(startDate);
          launchDates.push(baseDate);
        } else {
          const previousDate = launchDates[index - 1];
          const offsetValue = launch.incrementalLaunchTimingValue.offsetValue || 0;
          const offsetUnit = launch.incrementalLaunchTimingValue.offsetUnit || 'Hours';

          let offsetInMs = 0;
          switch (offsetUnit.toLowerCase()) {
            case 'hours':
              offsetInMs = offsetValue * 60 * 60 * 1000;
              break;
            case 'days':
              offsetInMs = offsetValue * 24 * 60 * 60 * 1000;
              break;
            case 'weeks':
              offsetInMs = offsetValue * 7 * 24 * 60 * 60 * 1000;
              break;
            case 'months':
              offsetInMs = offsetValue * 30 * 24 * 60 * 60 * 1000;
              break;
            default:
              offsetInMs = offsetValue * 60 * 60 * 1000;
          }

          const newDate = new Date(previousDate.getTime() + offsetInMs);

          if (launch.incrementalLaunchTimingValue.releaseTime && offsetUnit.toLowerCase() !== 'hours') {
            const releaseTimeDate = new Date(launch.incrementalLaunchTimingValue.releaseTime);
            newDate.setHours(releaseTimeDate.getHours());
            newDate.setMinutes(releaseTimeDate.getMinutes());
            newDate.setSeconds(releaseTimeDate.getSeconds());
            newDate.setMilliseconds(releaseTimeDate.getMilliseconds());
          }

          launchDates.push(newDate);
        }
      });

      return launchDates;
    };

    scheduleGroups.forEach((scheduleGroup) => {
      const schedulePostData: GloabalSchedulePostData = {
        customer_id: 1,
        platforms: selectedPlatforms.map((p) => p.toLowerCase()),
        schedulings: []
      };
      scheduleGroup.forEach((schedule) => {
        const launchDates = calculateLaunchDates(schedule);
        const scheduleVersionId = schedule.versionId;

        let versionIncluded = true;

        // Initialize launch values once per schedule - these will accumulate across launches
        let launchTerritory = schedule.selectedTerritories || selectedTerritories;
        let launchAudioLanguages = schedule.selectedDubs?.map((dub) => dub.toLowerCase()) || selectedDubs.map((d) => d.toLowerCase());
        let launchTimedTextLanguages = schedule.selectedSubs?.map((sub) => sub.toLowerCase()) || selectedSubs.map((s) => s.toLowerCase());

        launchList.forEach((launch, launchIndex) => {
          const addVersionIds = launch.incrementalLaunchVersions.add || [];
          const removeVersionIds = launch.incrementalLaunchVersions.remove || [];

          if (removeVersionIds.includes(scheduleVersionId)) {
            versionIncluded = false;
          }
          if (addVersionIds.includes(scheduleVersionId)) {
            versionIncluded = true;
          }

          if (!versionIncluded) {
            return;
          }

          const launchTerritories = launch.incrementalLaunchRightsDraftByTab.territories?.add || [];
          const launchAudioRights = launch.incrementalLaunchRightsDraftByTab.audio?.add || [];
          const launchTimedTextRights = launch.incrementalLaunchRightsDraftByTab.timedtext?.add || [];

          // Accumulate rights for this launch (always additive to maintain across launches)
          if (launchTerritories.length > 0) {
            launchTerritory = Array.from(new Set([...launchTerritory, ...launchTerritories.map((t) => t.toLowerCase())]));
          }
          if (launchAudioRights.length > 0) {
            launchAudioLanguages = Array.from(new Set([...launchAudioLanguages, ...launchAudioRights.map((lang) => lang.toLowerCase())]));
          }
          if (launchTimedTextRights.length > 0) {
            launchTimedTextLanguages = Array.from(new Set([...launchTimedTextLanguages, ...launchTimedTextRights.map((lang) => lang.toLowerCase())]));
          }

          const launchStartDate = launchDates[launchIndex].toISOString();
          schedulePostData.schedulings.push({
            version_id: scheduleVersionId,
            primary_av_lang: 'ja-jp',
            license: selectedLicense,
            window_start: launchStartDate,
            window_end: endDate || defaultAvailEndDate,
            territory: launchTerritory.map((t) => t.toLowerCase()),
            audio_languages: launchAudioLanguages,
            timed_text_languages: launchTimedTextLanguages
          });
        });
        const newRequest = axios.post('focus/global-schedule/create/', schedulePostData, { baseURL: '/api/v1.1' });
        scheduleRequests.push(newRequest);
      });
    });

    if (scheduleRequests.length === 0) {
      error('No schedule requests could be created. Please check your configuration and try again.');
      setCreatingSchedule(false);
      return;
    }

    await Promise.all(scheduleRequests)
      .then(() => {
        success('Schedule created successfully');
        history.push('/focus/schedule/');
        onClose();
        dispatch(getData());
      })
      .catch((err) => {
        console.error('Error creating schedule:', err);
        error('Error creating schedule');
        setCreatingSchedule(false);
      });
  };

  const onClose = () => {
    setIsIncrementalLaunchWorkspaceOpen(false);
    dispatch(clearNewSchedule());
    dispatch(clearTitle());
    dispatch(clearOrderPlacement(undefined));
    dispatch(setActiveTitleId(null));
    dispatch(setActiveTitle(null));
  };

  useEffect(() => {
    if (view !== 'review' && isIncrementalLaunchWorkspaceOpen) {
      setIsIncrementalLaunchWorkspaceOpen(false);
    }
  }, [view, isIncrementalLaunchWorkspaceOpen]);

  useEffect(() => {
    return () => {
      onClose();
    };
  }, []);

  return (
    <>
      <NewScheduleTitle />
      <StyledContainer>
        <ScrollingContainer>
          {view === 'search' && <OPTitleSearch />}
          {view === 'details' && <ScheduleDetails />}
          {view === 'review' && <ReviewSchedule onIncrementalLaunchWorkspaceOpenChange={setIsIncrementalLaunchWorkspaceOpen} />}
        </ScrollingContainer>
        <ActionContainer>
          <div>
            <Button disabled={view === 'search'} variant="outlined" onClick={handleBackClick}>
              Go Back
            </Button>
          </div>
          <ButtonGroup>
            <Button variant="outlined" onClick={() => setDialogVisible(true)}>
              Cancel
            </Button>
            <Button disabled={isNextDisabled()} variant="contained" onClick={handleNextClick}>
              {view === 'review' ? 'Save Draft Schedule' : 'Next'}
            </Button>
          </ButtonGroup>
        </ActionContainer>
      </StyledContainer>
      <ActionDialog
        open={isBackConfirmOpen}
        title="If you go back your changes will be lost."
        primaryActionLabel="Go Back"
        onPrimaryAction={handleConfirmBack}
        secondaryActionLabel="Stay"
        onSecondaryAction={handleCloseBackConfirm}
        onClose={handleCloseBackConfirm}
      />
      {dialogVisible && dialog}
    </>
  );
};

export default NewSchedule;

const StyledContainer = styled(Container)`
  background: ${({ theme }) => (theme as FocusTheme).palette.neutral[100]};
  padding: ${({ theme }) => theme.spacing(4)} 0;
  height: calc(100vh - 250px);
  min-height: 400px;
`;

const ScrollingContainer = styled.div`
  height: calc(100% - 80px);
  overflow: auto;
`;

const ActionContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(2)} 0;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
`;
